import React, { useState, useEffect } from 'react';
import { Typography, Row, Col, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { storefrontApi, type AboutData, type AboutStoryBlock } from '@/apis/storefrontApi';
import { getStorefrontIcon } from '@/utils/storefrontIcons';
import styles from './index.module.less';

const { Title, Paragraph } = Typography;

const DEFAULT_BLOCKS = [
  {
    icon: 'ThunderboltOutlined',
    title: 'Quality Products',
    text: 'We source and supply reliable small appliances, electrical equipment, hardware and solar solutions.',
  },
  {
    icon: 'TeamOutlined',
    title: 'Expert Support',
    text: 'Our team provides technical guidance and after-sales support.',
  },
  {
    icon: 'SafetyOutlined',
    title: 'Trusted Partner',
    text: 'We work with manufacturers and distributors to ensure compliance and consistent supply.',
  },
];

const About: React.FC = () => {
  const { i18n } = useTranslation();
  const [data, setData] = useState<AboutData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    storefrontApi
      .getAbout()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [i18n.language]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingWrap}>
          <Spin size='large' />
        </div>
      </div>
    );
  }

  const heroImage = data?.heroImage?.trim() || '';
  const title = data?.title ?? 'About Us';
  const lead =
    data?.lead ??
    'YingC Enrich focuses on small appliances, electrical, hardware and photovoltaic products.';
  const blocks = data?.blocks?.length ? data.blocks : DEFAULT_BLOCKS;
  const storyBlocks = (data?.storyBlocks ?? []).filter(
    (item) => item.image || item.title || item.text
  );
  const factoryTitle = data?.factoryDisplay?.title || 'Factory Display';
  const factoryImages = (data?.factoryDisplay?.images ?? []).filter(Boolean).slice(0, 6);
  const content = data?.content;

  return (
    <div className={styles.page}>
      <section className={styles.section}>
        <div className={styles.inner}>
          {heroImage && (
            <div className={styles.heroMedia}>
              <img src={heroImage} alt={title} className={styles.heroImg} />
            </div>
          )}
          <Title level={2} className={styles.sectionTitle}>
            {title}
          </Title>
          {lead && (
            <Paragraph className={styles.sectionDesc}>{lead}</Paragraph>
          )}

          <Row gutter={[24, 24]} className={styles.points}>
            {blocks.map((p, i) => (
              <Col xs={24} md={8} key={`${p.title}-${i}`}>
                <div className={styles.point}>
                  <div className={styles.pointIcon}>{getStorefrontIcon(p.icon)}</div>
                  <Title level={4} className={styles.pointTitle}>
                    {p.title}
                  </Title>
                  <Paragraph className={styles.pointText}>{p.text}</Paragraph>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {storyBlocks.map((block, i) => (
        <StoryBlock key={`${block.title}-${i}`} block={block} />
      ))}

      {factoryImages.length > 0 && (
        <section className={styles.section}>
          <div className={styles.inner}>
            <Title level={2} className={styles.sectionTitle}>
              {factoryTitle}
            </Title>
            <Row gutter={[16, 16]}>
              {factoryImages.map((src, i) => (
                <Col xs={24} sm={12} md={8} key={`${src}-${i}`}>
                  <div className={styles.factoryMedia}>
                    <img src={src} alt={`${factoryTitle} ${i + 1}`} className={styles.factoryImg} />
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </section>
      )}

      {content && (
        <section className={styles.section}>
          <div className={styles.inner}>
            <div
              className={styles.content}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
        </section>
      )}
    </div>
  );
};

function StoryBlock({ block }: { block: AboutStoryBlock }) {
  const imageRight = block.imagePosition === 'right';
  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div className={`${styles.storyRow} ${imageRight ? styles.storyRowReverse : ''}`}>
          <div className={styles.storyMedia}>
            {block.image ? (
              <img src={block.image} alt={block.title || ''} className={styles.storyImg} />
            ) : (
              <div className={styles.storyPlaceholder} />
            )}
          </div>
          <div className={styles.storyCopy}>
            {block.title && (
              <Title level={2} className={styles.storyTitle}>
                {block.title}
              </Title>
            )}
            {block.text && <p className={styles.storyText}>{block.text}</p>}
          </div>
        </div>
      </div>
    </section>
  );
}

export default About;
